import { Box, Button, Stack } from "@mui/material";
import LogChat from "./LogChat";
import PlaningToolRoot, { State } from "../PlaningToolRoot";
import { MItem } from "../Menu";
import InputB from "../../../components/InputB/InputB";
import { useEffect, useState } from "react";
import { GLItemKind, GLItemStatus, GLItemStatuses } from "../../../DTO/GLItemDTO";
import { GLItemsDTO } from "../../../DTO/GLItemsDTO";
import { Help } from "../Help";
import LogChatAmountDisplay from "./LogChatAmountDisplay";
import LogChatDetail from "./LogChatDetail";
import mainservice from "../../../services/MainService";

function statusTabFromNav(): GLItemStatus | undefined {
  const raw = `${mainservice.nav.getVal('status') || ''}`
  if (raw === 'all') {
    return undefined
  }
  if (raw === '') {
    return GLItemStatus.generic
  }
  const n = parseInt(raw, 10)
  return Number.isNaN(n) ? GLItemStatus.generic : n
}

function writeStatusToNav(status: GLItemStatus | undefined) {
  if (status === undefined) {
    mainservice.nav.setVal('status', 'all')
  } else {
    mainservice.nav.setVal('status', String(status))
  }
}

export default function LogChatCenter(props: {
  standalone?: boolean,
  className?: string,
  headline?: string,
  kind?: GLItemKind,
  menuItem?: MItem,
  useAlarmButton?: boolean,
  usePersonButton?: boolean,
}) {
  const [needle, setNeedle] = useState('')
  const [statusTab, setStatusTab] = useState<GLItemStatus | undefined>(() => statusTabFromNav())
  const [chatDetailId, setChatDetailId] = useState<number>(-1)
  const [scrollToId, setScrollToId] = useState<number>(-1)
  const [userId, setUserId] = useState<number>(-1)
  const [pastduedate, setPastDueDate] = useState<boolean>(false)
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({})
  const [statusCountsAll, setStatusCountsAll] = useState(0)
  const [countsDto] = useState(() => new GLItemsDTO({
    kind: props.kind,
    userId: -1,
    pastduedate: false,
  }))

  useEffect(() => {
    writeStatusToNav(statusTabFromNav())
    const urlId = mainservice.nav.getVal('id') as number
    if (urlId > -1) {
      openThreadFromUrl(urlId)
    }
  }, [])

  async function openThreadFromUrl(id: number) {
    try {
      const list = new GLItemsDTO({ id, page: 1, limit: 1 })
      await list.getFromServer()
      const item = list.items[0]
      if (!item) {
        setChatDetailId(id)
        setScrollToId(id)
        return
      }
      const rootId = item.parentId && item.parentId > 0 ? item.parentId : item.id
      setChatDetailId(rootId)
      setScrollToId(rootId)
    } catch (e) {
      console.error('Failed to open thread from URL', e)
      setChatDetailId(id)
      setScrollToId(id)
    }
  }

  useEffect(() => {
    let cancelled = false
    async function loadCounts() {
      await countsDto.getStatusCountsFromServer({
        kind: props.kind,
        userId,
        pastduedate,
      })
      if (cancelled) { return }
      setStatusCounts({ ...countsDto.statusCounts })
      setStatusCountsAll(countsDto.statusCountsAll)
    }
    loadCounts()
    const intervalId = setInterval(loadCounts, 30000)
    return () => {
      cancelled = true
      clearInterval(intervalId)
    }
  }, [countsDto, props.kind, userId, pastduedate])

  function selectStatusTab(status: GLItemStatus | undefined) {
    setStatusTab(status)
    writeStatusToNav(status)
  }

  function openDetail(id: number) {
    setChatDetailId(id)
    setScrollToId(id)
    mainservice.nav.setVal('id', id)
  }

  function closeDetail() {
    setChatDetailId(-1)
    setScrollToId(-1)
    mainservice.nav.unsetVal('id')
  }

  const onAlarmButton = props.useAlarmButton ? () => {
      setPastDueDate(!pastduedate)
  } : undefined
  const onPersonButton = props.usePersonButton ? () => {
      if (userId === -1) {
        setUserId(mainservice.getUserId())
      } else {
        setUserId(-1)
      }
  } : undefined
  const className = `LogChatCenter flex flex-column ${props.className || ''}`
  return <PlaningToolRoot
      standalone={true}
      menuItem={props.menuItem === undefined ? MItem.logChat : props.menuItem}
      mainContentClass={className}
      rightDrawer={<LogChatDetail
        id={chatDetailId}
        onClose={closeDetail}
      />}
      startStateRightDrawer={chatDetailId > -1 ? State.open : State.closed}
    >
      <div className='contentHeader'>
        <h2>{props.headline || 'Central Log'}</h2>
        <Stack
          spacing={2}
        >
          <Stack
            direction='row'
            spacing={2}
          >
            <InputB
              className='flex-grow'
              returnVal={async (val) => {
                setNeedle(val)
              }}
              placeholder={'Suchen...'}
              value={needle}
              clearButton={true}
              sendButton="Suchen"
              delay={-1}
            />
            <Help>
              <h2>Suchfunktionen im Überblick</h2>
              <div>Einfach tippen und ein paar Sekunden warten. Dann werden automatisch die passenden Ergebnisse geladen.</div>
              <h3>Sonderfunktionen:</h3>
              <ul>
              <li><strong>@</strong>: Ort - Verwende dieses Präfix, um nach einem Ort des Unternehmens zu suchen</li>
              <li><strong>%</strong>: Unternehmen - Verwende dieses Präfix, um nach einem Unternehmen zu suchen</li>
              </ul>
            </Help>
          </Stack>
          {
            props.kind !== undefined && GLItemStatuses[props.kind].length > 0 && 
            <Stack
              direction={'row'}
              spacing={2}
              useFlexGap
              sx={{
                flexWrap: 'wrap',
              }}
            >
              {GLItemStatuses[props.kind].map(s => {
                if ((s as {hideInTabs?: boolean}).hideInTabs) {
                  return null
                }
                return <Button
                  key={`logchat-status-tab-${s.status}`}
                  variant={s.status === statusTab ? 'contained' : 'outlined'}
                  onClick={() => selectStatusTab(s.status)}>{s.name} <LogChatAmountDisplay
                    total={s.status === undefined ? statusCountsAll : (statusCounts[String(s.status)] || 0)}
                  /></Button>
                })}
            </Stack>
          }
        </Stack>
      </div>
      <Box
        sx={{
          padding: 2,
          backgroundColor: 'white',
        }}
      >
        <LogChat
            disableNewMsg
            needle={needle}
            kind={props.kind}
            statusKind={statusTab}
            onClickDetail={openDetail}
            markedId={chatDetailId}
            scrollToId={scrollToId}
            onAlarmButton={onAlarmButton}
            onPersonButton={onPersonButton}
            userId={userId}
            pastduedate={pastduedate}
        />
      </Box>
    
  </PlaningToolRoot>
}
